import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Divider, Drawer, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { ConfirmationModalType, DISCARD_CHANGES_MESSAGE } from 'constants/confirmationDialog';

import { openConfirmationDialogAction } from 'store/global/actions';

import { CancelIcon } from '../../icons';

import { DEFAULT_CANCEL_LABEL, DrawerSize } from './DrawerCDS.constants';
import {
  closeButtonStyles,
  contentStyles,
  controlsWrapperStyles,
  dividerStyles,
  formContentStyles,
  getDrawerProps,
  headerStyles,
  slideProps,
  titleStyles,
  wrapperStyles,
} from './DrawerCDS.styles';

/**
 * DrawerCDS - CDS slide-in drawer with fixed size enum, optional dirty-close guard.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Called when user closes without dirty guard triggering
 * @param {string} props.title - Header title text
 * @param {string} [props.subtitle] - Secondary line under the title
 * @param {React.ReactNode} props.content - Body content
 * @param {React.ReactNode} [props.actions] - Footer action buttons (Cancel auto-added)
 * @param {string} [props.cancelLabel] - Label of the auto-added Cancel button (e.g. "Close" on a read-only drawer)
 * @param {boolean} [props.isLoading] - Disables Cancel while loading
 * @param {string} [props.size] - DrawerSize.STANDARD or DrawerSize.SMALL
 * @param {boolean} [props.isDirty] - When true, close triggers a discard-changes confirm
 * @param {string} [props.dataTestId] - Test id forwarded onto the drawer paper element
 * @returns {React.ReactElement}
 */
const DrawerCDS = ({
  open,
  onClose,
  title,
  subtitle = '',
  content,
  actions = null,
  cancelLabel = DEFAULT_CANCEL_LABEL,
  isLoading = false,
  size = DrawerSize.STANDARD,
  isDirty = false,
  dataTestId,
  'data-testid': dataTestIdAttr,
}) => {
  const dispatch = useDispatch();

  const memoizedContent = useMemo(() => content, [content]);
  const memoizedActions = useMemo(() => actions, [actions]);

  const drawerProps = useMemo(() => getDrawerProps(size), [size]);

  const resolvedDataTestId = dataTestId || dataTestIdAttr || null;

  const paperProps = useMemo(() => ({ 'data-testid': resolvedDataTestId }), [resolvedDataTestId]);

  const onRequestClose = useCallback(() => {
    if (!isDirty) {
      onClose();

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: DISCARD_CHANGES_MESSAGE,
        onAccept: onClose,
      }),
    );
  }, [isDirty, onClose, dispatch]);

  return (
    <Drawer
      hideBackdrop
      anchor="right"
      open={open}
      onClose={onRequestClose}
      {...drawerProps}
      PaperProps={paperProps}
      SlideProps={slideProps}
    >
      <Box sx={contentStyles}>
        <Box sx={headerStyles}>
          <Box sx={titleStyles}>
            <Typography component="div" variant="bold16">
              {title}
            </Typography>
            {subtitle && (
              <Typography color="text.secondary" component="div" variant="regular12">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            disableRipple
            aria-label="Close"
            className="close-button"
            color="inherit"
            sx={closeButtonStyles}
            onClick={onRequestClose}
          >
            <CancelIcon />
          </IconButton>
        </Box>

        <Box sx={wrapperStyles}>
          <Box sx={formContentStyles}>{memoizedContent}</Box>

          {memoizedActions && (
            <>
              <Divider sx={dividerStyles} />
              <Box sx={controlsWrapperStyles}>
                <Button disabled={isLoading} variant="outlined" onClick={onRequestClose}>
                  {cancelLabel}
                </Button>
                {memoizedActions}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

DrawerCDS.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  cancelLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  size: PropTypes.string,
  isDirty: PropTypes.bool,
  dataTestId: PropTypes.string,
  'data-testid': PropTypes.string,
};

export default DrawerCDS;
